import { ShoppingCart, Plus, Minus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useCart from "@/hooks/user-orders/use-cart";
import useUpdateCart from "@/hooks/user-orders/use-update-cart";
import useCustomTranslation from "@/hooks/use-custom-translation";

interface CartItemProps {
  productName: string;
  quantity: number;
  productId: number;
  onIncrement: (productId: number) => void;
  onDecrement: (productId: number) => void;
  onRemove: (productId: number) => void;
}

function CartItem({
  productName,
  quantity,
  productId,
  onIncrement,
  onDecrement,
  onRemove,
}: CartItemProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-sm">{productName}</span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onDecrement(productId)}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="text-sm">{quantity}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onIncrement(productId)}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-destructive"
        onClick={() => onRemove(productId)}
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  );
}

export default function CartButton() {
  const { t } = useCustomTranslation();
  const { cart, isCartLoading } = useCart();
  const { incrementCartItem, decrementCartItem, removeCartItem } =
    useUpdateCart();

  if (isCartLoading || !cart || cart.length === 0) {
    return null;
  }

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className="rounded-full w-8 h-8 bg-background mr-2 relative"
          variant="outline"
          size="icon"
        >
          <ShoppingCart className="w-[1.2rem] h-[1.2rem]" />
          {totalItems > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              {totalItems}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80" align="end">
        <div className="space-y-4 flex flex-col gap-2">
          <h4 className="font-medium leading-none">{t("cart.title")}</h4>

          <div className="space-y-2">
            {cart.map((item) => (
              <CartItem
                key={item.id}
                productName={item.productName}
                quantity={item.quantity}
                productId={item.productId}
                onIncrement={incrementCartItem}
                onDecrement={decrementCartItem}
                onRemove={removeCartItem}
              />
            ))}
          </div>

          <a href="/cart" className="w-full">
            <Button className="w-full" variant="default">
              {t("cart.viewCart")}
            </Button>
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
}
